/**
 * NotificationsBell — topbar bell with real unack alert count + dropdown
 * preview of the top 5.
 *
 * Polls /api/device/statuses/ every 2 minutes (alerts are slow-changing
 * relative to /healthz). Pause when the tab is backgrounded so we don't
 * spam the shim for tabs the user isn't looking at.
 *
 * Quiet failure: if the call 401s (not logged in) or 412s (no Meraki key)
 * we render the bell silently with no count — the page-level data already
 * surfaces those cases. This component is a teaser, not the full alert UI.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  ExternalLink,
  AlertTriangle,
  AlertCircle,
  Info,
  Volume2,
  VolumeX,
  Monitor,
  MonitorOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import { get as apiGet, post as apiPost } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";
import { useRunNotifier } from "@/stores/run-notifier";

interface RawAlert {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  source: string;
  openedAt: string;
  ackedAt: string | null;
  vendor: string;
  target: string;
}

interface RawDeviceStatus {
  serial?: string;
  name?: string;
  model?: string;
  status?: string;
  networkId?: string;
  _orgName?: string;
  lastReportedAt?: string;
  ackedAt?: string | null;
  alertId?: string;
}

const POLL_INTERVAL_MS = 2 * 60_000; // 2 min — alerts move slowly

export function NotificationsBell() {
  const [alerts, setAlerts] = useState<RawAlert[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Workflow-completion notifier prefs — surfaced in this dropdown so the
  // user has one place for "notification stuff". Toggling these doesn't
  // affect alert acks, only the run-finished beep/desktop toast.
  const soundEnabled = useRunNotifier((s) => s.soundEnabled);
  const desktopEnabled = useRunNotifier((s) => s.desktopEnabled);
  const setSoundEnabled = useRunNotifier((s) => s.setSoundEnabled);
  const toggleDesktop = useRunNotifier((s) => s.toggleDesktop);

  const toAlerts = useCallback((rows: RawDeviceStatus[]): RawAlert[] => {
    const statusToSeverity: Record<string, RawAlert["severity"]> = {
      offline: "critical",
      alerting: "warning",
      dormant: "info",
    };

    return rows
      .map((row) => {
        const status = String(row.status ?? "").toLowerCase();
        if (!status || status === "online") return null;
        const serial = String(row.serial ?? "");
        const id = row.alertId || (serial ? `alert-meraki-${serial}` : "");
        if (!id) return null;

        return {
          id,
          severity: statusToSeverity[status] ?? "info",
          title: `${String(row.name ?? serial ?? "device")} (${String(row.model ?? "?")}) is ${status}`,
          source: `meraki/${String(row._orgName ?? "")}`,
          openedAt: String(row.lastReportedAt ?? ""),
          ackedAt: row.ackedAt ?? null,
          vendor: "meraki",
          target: String(row.networkId ?? "(no network)"),
        };
      })
      .filter((row): row is RawAlert => row !== null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const raw = await apiGet<RawDeviceStatus[]>("/device/statuses/");
      setAlerts(Array.isArray(raw) ? toAlerts(raw) : []);
    } catch {
      // Quiet — see file header. Don't surface 401/412 in this teaser.
    } finally {
      setLoading(false);
    }
  }, [toAlerts]);

  useEffect(() => {
    let cancelled = false;
    let visible = !document.hidden;

    const probe = () => {
      if (cancelled || !visible) return;
      void refresh();
    };
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
    const id = window.setInterval(probe, POLL_INTERVAL_MS);
    const onVis = () => {
      visible = !document.hidden;
      if (visible) probe();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  const unack = useMemo(() => alerts.filter((a) => !a.ackedAt), [alerts]);
  const top5 = useMemo(() => unack.slice(0, 5), [unack]);
  const count = unack.length;

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest?.("[data-notif-root]")) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  async function ack(id: string) {
    // Optimistic update — flip the row before the round-trip lands. The
    // store/page that owns the same data will reconcile on next refresh.
    setAlerts((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, ackedAt: new Date().toISOString() } : a,
      ),
    );
    try {
      await apiPost(`/monitor/alerts/${id}/ack/`);
    } catch {
      // Revert by re-fetching — keeps us aligned with server truth.
      void refresh();
    }
  }

  /** Ack every currently-unacknowledged alert in one round-trip. The
   *  backend takes a list rather than a magic "all" flag so the UI can
   *  always show the user EXACTLY which ids are about to be acked
   *  (matches the optimistic update below). */
  async function ackAll() {
    const ids = unack.map((a) => a.id);
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    const idSet = new Set(ids);
    setAlerts((prev) =>
      prev.map((a) => (idSet.has(a.id) ? { ...a, ackedAt: now } : a)),
    );
    try {
      await apiPost("/monitor/alerts/bulk-ack/", { alert_ids: ids });
    } catch {
      // Resync from the server on failure so the UI doesn't lie.
      void refresh();
    }
  }

  return (
    <div data-notif-root className="relative">
      <button
        type="button"
        aria-label={`Notifications (${count} unacknowledged)`}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface",
          "text-fg-dim hover:bg-surface-2 hover:text-fg",
        )}
      >
        <Bell size={16} />
        {/* Count badge — only shows when there's something to act on. */}
        {count > 0 ? (
          <span
            className={cn(
              "absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center",
              "rounded-full bg-rose px-1 font-mono text-[9px] font-bold text-white ring-2 ring-bg-0",
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            "absolute right-0 top-[calc(100%+8px)] z-50 w-[360px] overflow-hidden",
            "rounded-lg border border-line bg-surface shadow-2xl",
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
            <div className="text-[12px] font-semibold text-fg">
              Active alerts{" "}
              {count > 0 ? (
                <span className="font-mono text-fg-mute">· {count}</span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {/* Bulk-ack — only enabled when there's something to ack.
                  Goes through one /bulk-ack endpoint so a user with 30
                  alerts doesn't trigger 30 audit-log rows. */}
              {count > 0 ? (
                <button
                  type="button"
                  onClick={() => void ackAll()}
                  title={`Acknowledge all ${count} alerts`}
                  className="text-[11px] font-semibold text-fg-dim hover:text-mint"
                >
                  Ack all
                </button>
              ) : null}
              <Link
                to="/monitor"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 text-[11px] text-violet hover:underline"
              >
                View all <ExternalLink size={10} />
              </Link>
            </div>
          </div>

          {loading && alerts.length === 0 ? (
            <div className="px-3 py-6 text-center text-[12px] text-fg-mute">
              Loading…
            </div>
          ) : top5.length === 0 ? (
            <div className="px-3 py-6 text-center text-[12px] text-fg-mute">
              {alerts.length === 0
                ? "No alerts in scope"
                : "All clear — nothing unacknowledged"}
            </div>
          ) : (
            <ul className="m-0 max-h-[320px] list-none overflow-y-auto p-0">
              {top5.map((a) => (
                <li
                  key={a.id}
                  className="flex items-start gap-2 border-t border-line/60 px-3 py-2 first:border-t-0"
                >
                  <SeverityIcon severity={a.severity} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-semibold text-fg">
                      {a.title}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-fg-mute">
                      <span>{a.source}</span>
                      <span>·</span>
                      <Badge variant="neutral" size="sm">
                        {a.vendor}
                      </Badge>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void ack(a.id)}
                    className="text-[10px] font-semibold text-fg-dim hover:text-mint"
                  >
                    ack
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Run-completion notification prefs — toggled here so users
              have one place for all "notify me" controls. */}
          <div className="flex items-center justify-between gap-2 border-t border-line bg-surface-tint/40 px-3 py-2 text-[11px] text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Notify on workflow finish
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setSoundEnabled(!soundEnabled)}
                title={
                  soundEnabled
                    ? "Sound on — click to mute"
                    : "Sound off — click to enable"
                }
                className={cn(
                  "inline-flex h-6 w-6 items-center justify-center rounded-md border border-line bg-surface",
                  soundEnabled ? "text-mint" : "text-fg-mute hover:text-fg",
                )}
              >
                {soundEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
              </button>
              <button
                type="button"
                onClick={() => void toggleDesktop()}
                title={
                  desktopEnabled
                    ? "Desktop notifications on — click to disable"
                    : "Desktop off — click to request browser permission"
                }
                className={cn(
                  "inline-flex h-6 w-6 items-center justify-center rounded-md border border-line bg-surface",
                  desktopEnabled ? "text-mint" : "text-fg-mute hover:text-fg",
                )}
              >
                {desktopEnabled ? (
                  <Monitor size={12} />
                ) : (
                  <MonitorOff size={12} />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SeverityIcon({ severity }: { severity: RawAlert["severity"] }) {
  if (severity === "critical")
    return <AlertCircle size={14} className="mt-0.5 shrink-0 text-rose" />;
  if (severity === "warning")
    return <AlertTriangle size={14} className="mt-0.5 shrink-0 text-peach" />;
  return <Info size={14} className="mt-0.5 shrink-0 text-violet" />;
}
